import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import authService from '../services/authService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setLoading(false);
        return;
      }
      const { data } = await api.get('/profile');
      if (data.success) {
        setUser(data.data.user);
      }
      // Restore tenant from localStorage
      const savedTenant = localStorage.getItem('tenant');
      if (savedTenant) {
        try { setTenant(JSON.parse(savedTenant)); } catch (e) { /* ignore */ }
      }
    } catch (err) {
      // Only nuke the session on genuine auth failures (401). The previous
      // catch-all wiped the user on any network blip / 5xx / CORS hiccup —
      // making the app log itself out under transient flakiness. Network
      // errors leave the existing session intact and the page can retry.
      const status = err?.response?.status;
      const isAuthFailure = status === 401 || status === 403;
      if (isAuthFailure) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('tenant');
        setUser(null);
        setTenant(null);
      } else {
        // Keep whatever session state we have and let the user retry.
        console.warn('[AuthContext] fetchProfile failed (non-auth):', err?.message || err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchProfile();
  }, [fetchProfile]);

  const login = useCallback(async (email, password, captchaToken) => {
    const { data } = await authService.login(email, password, captchaToken);
    if (data.success) {
      localStorage.setItem('accessToken', data.data.accessToken);
      setUser(data.data.user);
      if (data.data.tenant) {
        setTenant(data.data.tenant);
        localStorage.setItem('tenant', JSON.stringify(data.data.tenant));
      }
    }
    return data;
  }, []);

  const register = useCallback(async (formData) => {
    const { data } = await authService.completeRegistration(formData);
    if (data.success) {
      localStorage.setItem('accessToken', data.data.accessToken);
      setUser(data.data.user);
      if (data.data.tenant) {
        setTenant(data.data.tenant);
        localStorage.setItem('tenant', JSON.stringify(data.data.tenant));
      }
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (e) {
      // ignore
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('tenant');
    setUser(null);
    setTenant(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
  }, []);

  // Stable context value — without useMemo every consumer of useAuth()
  // re-rendered whenever ANY ancestor re-rendered (because the object literal
  // was a new identity each time). With strict context equality, this was
  // forcing a cascade through the dashboard layout, the inbox, etc.
  const value = useMemo(
    () => ({ user, tenant, loading, login, register, logout, updateUser, fetchProfile }),
    [user, tenant, loading, login, register, logout, updateUser, fetchProfile]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
